import React, { Component } from "react";
import {
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Calendar,
  Hash,
  DollarSign,
  Calculator,
  Tag,
  Settings,
  ArrowRight,
} from "lucide-react";
import "../styles/GlobalAdvancedSearchBar.css";

class GlobalAdvancedSearchBar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isAdvancedOpen: false,
      filters: {
        // Basic search
        textToSearch: "",

        // Number filters
        numberStartsWith: "",
        numberEndsWith: "",
        numberContains: "",
        numberExact: "",

        // Price filters
        priceMin: "",
        priceMax: "",
        priceExact: "",

        // Sum filters
        sumOne: "",
        sumTwo: "",
        sumTotal: "",

        // Category filters
        categoryId: "",
        categoryName: "",

        // Status filters
        status: [2], // Default to active

        // Date filters
        startDate: "",
        endDate: "",

        // Sorting
        sortByPrice: "",
        sortBySeries: "",

        // Pagination
        limit: 20,

        // Advanced text filters
        mustContain: "",
        mustNotContain: "",
      },
    };
  }

  componentDidMount() {
    // If we're already on the numbers page, initialize from URL
    if (window.location.pathname.includes("/numbers")) {
      this.initializeFromURL();
    }
  }

  initializeFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const newFilters = { ...this.state.filters };

    // Parse URL parameters and set filters
    for (let [key, value] of urlParams.entries()) {
      if (key === "searchBarText" && value !== "true") {
        newFilters.textToSearch = value;
      } else if (key === "status") {
        newFilters.status = value
          .split(",")
          .map((s) => parseInt(s))
          .filter((s) => !isNaN(s));
      } else if (key === "categoryId") {
        newFilters.categoryId = value;
      } else if (key === "categoryName") {
        newFilters.categoryName = value;
      } else if (key === "sumOne") {
        newFilters.sumOne = value;
      } else if (key === "sumTwo") {
        newFilters.sumTwo = value;
      } else if (key === "sumTotal") {
        newFilters.sumTotal = value;
      } else if (key === "numberExact") {
        newFilters.numberExact = value;
      } else if (key === "numberStartsWith") {
        newFilters.numberStartsWith = value;
      } else if (key === "numberEndsWith") {
        newFilters.numberEndsWith = value;
      } else if (key === "numberContains") {
        newFilters.numberContains = value;
      } else if (key === "priceExact") {
        newFilters.priceExact = value;
      } else if (key === "priceMin") {
        newFilters.priceMin = value;
      } else if (key === "priceMax") {
        newFilters.priceMax = value;
      } else if (key === "startDate") {
        newFilters.startDate = value;
      } else if (key === "endDate") {
        newFilters.endDate = value;
      } else if (key === "sortByPrice") {
        newFilters.sortByPrice = value;
      } else if (key === "sortBySeries") {
        newFilters.sortBySeries = value;
      } else if (key === "limit") {
        newFilters.limit = parseInt(value) || 20;
      } else if (newFilters.hasOwnProperty(key)) {
        newFilters[key] = value;
      }
    }

    this.setState({ filters: newFilters });
  };

  handleInputChange = (field, value) => {
    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        [field]: value,
      },
    }));
  };

  buildURLParams = () => {
    const { filters } = this.state;
    const params = new URLSearchParams();

    // Only add non-empty filters to URL
    Object.keys(filters).forEach((key) => {
      const value = filters[key];

      if (value !== "" && value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            if (key === "status") {
              params.set(key, value.join(","));
            } else {
              params.set(key, value.join(","));
            }
          }
        } else {
          params.set(key, value.toString());
        }
      }
    });

    // Handle special cases for backward compatibility
    if (filters.textToSearch) {
      params.set("searchBarText", filters.textToSearch);
    }

    return params.toString();
  };

  navigateToNumbers = () => {
    const urlParams = this.buildURLParams();
    const numbersURL = `/numbers${urlParams ? "?" + urlParams : ""}`;

    // Navigate to numbers page with search parameters
    if (this.props.history) {
      // If using React Router with history prop
      this.props.history.push(numbersURL);
    } else {
      // Direct navigation
      window.location.href = numbersURL;
    }
  };

  handleSearch = () => {
    this.navigateToNumbers();
  };

  handleQuickSearch = () => {
    // For simple text search, just navigate with textToSearch parameter
    if (this.state.filters.textToSearch) {
      const params = new URLSearchParams();
      params.set("searchBarText", this.state.filters.textToSearch);

      if (this.props.history) {
        this.props.history.push(`/numbers?${params.toString()}`);
      } else {
        window.location.href = `/numbers?${params.toString()}`;
      }
    }
  };

  handleKeyDown = (e) => {
    if (e.key === "Enter") {
      this.handleQuickSearch();
    }
  };

  clearFilters = () => {
    this.setState({
      filters: {
        textToSearch: "",
        numberStartsWith: "",
        numberEndsWith: "",
        numberContains: "",
        numberExact: "",
        priceMin: "",
        priceMax: "",
        priceExact: "",
        sumOne: "",
        sumTwo: "",
        sumTotal: "",
        categoryId: "",
        categoryName: "",
        status: [2],
        startDate: "",
        endDate: "",
        sortByPrice: "",
        sortBySeries: "",
        limit: 20,
        mustContain: "",
        mustNotContain: "",
      },
    });
  };

  toggleAdvanced = () => {
    this.setState((prevState) => ({
      isAdvancedOpen: !prevState.isAdvancedOpen,
    }));
  };

  renderBasicSearch = () => {
    const isOnNumbersPage = window.location.pathname.includes("/numbers");

    return (
      <div className="basic-search-container">
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="Search numbers, price, sum..."
            value={this.state.filters.textToSearch}
            onChange={(e) =>
              this.handleInputChange("textToSearch", e.target.value)
            }
            onKeyDown={this.handleKeyDown}
            className="search-input"
          />
          <Search className="search-icon" />
        </div>
        <button onClick={this.toggleAdvanced} className="advanced-toggle-btn">
          <Filter className="btn-icon" />
          <span className="btn-text">Advanced</span>
          {this.state.isAdvancedOpen ? (
            <ChevronUp className="chevron-icon" />
          ) : (
            <ChevronDown className="chevron-icon" />
          )}
        </button>
        <button onClick={this.handleQuickSearch} className="search-btn">
          <Search className="btn-icon" />
          <span className="btn-text">Search</span>
          {!isOnNumbersPage && <ArrowRight className="chevron-icon" />}
        </button>
      </div>
    );
  };

  renderAdvancedFilters = () => {
    if (!this.state.isAdvancedOpen) return null;

    const { filters } = this.state;
    const isOnNumbersPage = window.location.pathname.includes("/numbers");

    return (
      <div className="advanced-filters-container">
        <div className="advanced-header">
          <h3 className="advanced-title">
            <Settings className="title-icon" />
            <span>Advanced Filters</span>
          </h3>
          <button onClick={this.clearFilters} className="clear-filters-btn">
            <X className="clear-icon" />
            <span>Clear All</span>
          </button>
        </div>

        {/* Number Filters */}
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <Hash className="label-icon" />
              <span>Number Filters</span>
            </label>
            <input
              type="text"
              placeholder="Exact number"
              value={filters.numberExact}
              onChange={(e) =>
                this.handleInputChange("numberExact", e.target.value)
              }
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Starts with"
              value={filters.numberStartsWith}
              onChange={(e) =>
                this.handleInputChange("numberStartsWith", e.target.value)
              }
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Ends with"
              value={filters.numberEndsWith}
              onChange={(e) =>
                this.handleInputChange("numberEndsWith", e.target.value)
              }
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Contains"
              value={filters.numberContains}
              onChange={(e) =>
                this.handleInputChange("numberContains", e.target.value)
              }
              className="filter-input"
            />
          </div>

          {/* Price Filters */}
          <div className="filter-group">
            <label className="filter-label">
              <DollarSign className="label-icon" />
              <span>Price Filters</span>
            </label>
            <input
              type="text"
              placeholder="Exact price"
              value={filters.priceExact}
              onChange={(e) =>
                this.handleInputChange("priceExact", e.target.value)
              }
              className="filter-input"
            />
            <input
              type="number"
              placeholder="Min price"
              value={filters.priceMin}
              onChange={(e) =>
                this.handleInputChange("priceMin", e.target.value)
              }
              className="filter-input"
            />
            <input
              type="number"
              placeholder="Max price"
              value={filters.priceMax}
              onChange={(e) =>
                this.handleInputChange("priceMax", e.target.value)
              }
              className="filter-input"
            />
          </div>

          {/* Sum Filters */}
          <div className="filter-group">
            <label className="filter-label">
              <Calculator className="label-icon" />
              <span>Sum Filters</span>
            </label>
            <input
              type="text"
              placeholder="Sum One (comma separated)"
              value={filters.sumOne}
              onChange={(e) => this.handleInputChange("sumOne", e.target.value)}
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Sum Two (comma separated)"
              value={filters.sumTwo}
              onChange={(e) => this.handleInputChange("sumTwo", e.target.value)}
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Sum Total"
              value={filters.sumTotal}
              onChange={(e) =>
                this.handleInputChange("sumTotal", e.target.value)
              }
              className="filter-input"
            />
          </div>

          {/* Category Filters */}
          <div className="filter-group">
            <label className="filter-label">
              <Tag className="label-icon" />
              <span>Category Filters</span>
            </label>
            <input
              type="text"
              placeholder="Category IDs (comma separated)"
              value={filters.categoryId}
              onChange={(e) =>
                this.handleInputChange("categoryId", e.target.value)
              }
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Category Names (comma separated)"
              value={filters.categoryName}
              onChange={(e) =>
                this.handleInputChange("categoryName", e.target.value)
              }
              className="filter-input"
            />
          </div>
        </div>

        {/* Search Button for Advanced Filters */}
        <div className="advanced-search-btn-container">
          <button onClick={this.handleSearch} className="advanced-search-btn">
            <Search className="btn-icon" />
            <span>Search with Advanced Filters</span>
            {!isOnNumbersPage && <ArrowRight className="chevron-icon" />}
          </button>
        </div>
      </div>
    );
  };

  render() {
    return (
      <div className="search-bar-wrapper">
        {this.renderBasicSearch()}
        {this.renderAdvancedFilters()}
      </div>
    );
  }
}

export default GlobalAdvancedSearchBar;
